import { Paper, TextField, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { DirectShearGeneral, DirectShearStage } from '../../types/directShear';
import { CommonParametersForm } from './components/CommonParametersForm';
import { StageParametersGrid } from './components/StageParametersGrid';
import { ObservationMediaPanel } from '../../components/observations/ObservationMediaPanel';

interface TestParametersTabProps {
  general: DirectShearGeneral;
  stages: DirectShearStage[];
  onGeneralChange: (field: string, value: string | number | null) => void;
  onStageChange: (stageIndex: number, field: string, value: number | null) => void;
  leebTop?: (number | null)[];
  leebBottom?: (number | null)[];
  onLeebChange?: (position: 'Top' | 'Bottom', readings: (number | null)[]) => void;
  readOnly?: boolean;
  workOrderDetailKey?: string;
}

export const TestParametersTab = ({
  general, stages, onGeneralChange, onStageChange,
  leebTop = [], leebBottom = [], onLeebChange = () => {},
  readOnly = false, workOrderDetailKey,
}: TestParametersTabProps) => {
  const theme = useTheme();
  return (
    <>
      <CommonParametersForm data={general} onChange={onGeneralChange}
        leebTop={leebTop} leebBottom={leebBottom} onLeebChange={onLeebChange} readOnly={readOnly} />
      <StageParametersGrid stages={stages} onChange={onStageChange} readOnly={readOnly} />

      {/* Additional Observations — lab-internal, never printed on the report.
          (Remarks above IS printed in the report's LABORATORY COMMENTS box.) */}
      <Paper
        elevation={0}
        sx={{
          p: 2, mt: 1, borderRadius: 2,
          border: `1px solid ${alpha(theme.border.color, 0.35)}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, fontSize: '0.9rem' }}>
          Additional Observations (internal — not printed on the report)
        </Typography>
        <TextField
          size="small" fullWidth multiline minRows={3}
          placeholder="Internal observations about specimen, test conditions, or equipment — for the lab and reviewer only."
          value={general.additionalObservations ?? ''}
          onChange={(e) => onGeneralChange('additionalObservations', e.target.value || null)}
          disabled={readOnly}
          sx={{
            '& .MuiInputBase-input': { fontSize: '0.8rem' },
          }}
        />
        {workOrderDetailKey && (
          <ObservationMediaPanel workOrderDetailKey={workOrderDetailKey} readOnly={readOnly} />
        )}
      </Paper>
    </>
  );
};
